import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
} from '@mui/material';
import {
  People,
  PersonAdd,
  Refresh,
  FileDownload,
  CheckCircle,
  Cancel,
  Edit,
  Phone,
  Email,
  Badge,
  Payment,
  Close,
  Save,
  PowerSettingsNew,
  LockReset,
} from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import DataTable, { Column, StatusChip, DateCell } from '../../../components/Common/DataTable';
import api from '../../../services/api';

interface ClientData {
  id: string;
  codigo: string;
  nome: string;
  dataNascimento: string | null;
  morada: string | null;
  estado: string;
  tipo: string;
  tipoId: string;
  username: string;
  email: string;
  emailContacto: string | null;
  telefone: string | null;
  pais: string | null;
  dataRegisto: string;
}

interface ClientStats {
  total: number;
  ativos: number;
  inativos: number;
  tipos: number;
}

interface ClientType {
  id: string;
  nome: string;
  codigo: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Clients: React.FC = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [clientTypes, setClientTypes] = useState<ClientType[]>([]);
  const [stats, setStats] = useState<ClientStats>({
    total: 0,
    ativos: 0,
    inativos: 0,
    tipos: 0,
  });

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [viewTab, setViewTab] = useState(0);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ nome: '', morada: '', email: '', telefone: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Reset password
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
      });

      const response = await api.get(`/admin/clients?${params}`);
      if (response.data.success) {
        setClients(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, typeFilter]);

  const fetchClientTypes = async () => {
    try {
      const response = await api.get('/admin/clients/types');
      if (response.data.success) {
        setClientTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching client types:', error);
      setClientTypes([]);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchClientTypes();
  }, [fetchClients]);

  const handleView = async (client: ClientData) => {
    setViewDialogOpen(true);
    setViewTab(0);
    setLoadingDetails(true);

    try {
      const response = await api.get(`/admin/clients/${client.id}`);
      if (response.data.success) {
        setSelectedClient(response.data.data);
      } else {
        setSelectedClient(client);
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
      setSelectedClient(client);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedClient(null);
    setEditMode(false);
  };

  const handleStartEdit = () => {
    if (!selectedClient) return;
    setEditForm({
      nome: selectedClient.nome || '',
      morada: selectedClient.morada || '',
      email: selectedClient.email || selectedClient.emailContacto || '',
      telefone: selectedClient.telefone || '',
    });
    setEditMode(true);
    setViewTab(0);
  };

  const handleSaveEdit = async () => {
    if (!selectedClient) return;
    setSavingEdit(true);
    setErrorMsg('');
    try {
      await api.put(`/admin/clients/${selectedClient.id}`, editForm);
      setSuccessMsg(t('clients.clientUpdated'));
      setEditMode(false);
      fetchClients();
      const response = await api.get(`/admin/clients/${selectedClient.id}`);
      if (response.data.success) setSelectedClient(response.data.data);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('clients.errorUpdating'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedClient) return;
    const newEstado = selectedClient.estado === 'A' ? 'I' : 'A';
    try {
      await api.put(`/admin/clients/${selectedClient.id}/status`, { estado: newEstado });
      setSuccessMsg(newEstado === 'A' ? t('clients.clientActivated') : t('clients.clientDeactivated'));
      setSelectedClient({ ...selectedClient, estado: newEstado });
      fetchClients();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('clients.errorStatus'));
    }
  };

  const handleResetPassword = async () => {
    if (!selectedClient) return;
    setResetLoading(true);
    setErrorMsg('');
    try {
      const response = await api.post(`/admin/clients/${selectedClient.id}/reset-password`);
      setResetConfirmOpen(false);
      setSuccessMsg(response.data.message || 'Email de redefinição enviado com sucesso');
    } catch (err: any) {
      setResetConfirmOpen(false);
      setErrorMsg(err?.response?.data?.error || err.message || 'Erro ao enviar email de reset');
    } finally {
      setResetLoading(false);
    }
  };

  const columns: Column<ClientData>[] = [
    {
      id: 'nome',
      label: t('common.client'),
      minWidth: 200,
      format: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: row?.estado === 'A' ? 'primary.main' : 'grey.400', width: 36, height: 36 }}>
            {value?.charAt(0)?.toUpperCase() || 'C'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row?.codigo}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'tipo',
      label: t('common.type'),
      minWidth: 100,
    },
    {
      id: 'email',
      label: t('common.email'),
      minWidth: 180,
      format: (value, row) => row?.emailContacto || value || '-',
    },
    {
      id: 'telefone',
      label: t('common.phone'),
      minWidth: 120,
      format: (value) => value || '-',
    },
    {
      id: 'pais',
      label: t('common.country'),
      minWidth: 100,
      format: (value) => value || 'Cabo Verde',
    },
    {
      id: 'dataRegisto',
      label: t('common.dateRegister'),
      minWidth: 110,
      format: (value) => <DateCell value={value} />,
    },
    {
      id: 'estado',
      label: t('common.status'),
      minWidth: 100,
      align: 'center',
      format: (value) => (
        <StatusChip
          status={value === 'A' ? t('common.active') : t('common.inactive')}
          colorMap={{
            [t('common.active')]: 'success',
            A: 'success',
            [t('common.inactive')]: 'default',
            I: 'default',
          }}
        />
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('clients.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('clients.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />}>
            {t('common.export')}
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchClients}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                  <People />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('clients.totalClients')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                  <CheckCircle />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.ativos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('clients.active')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.selected', color: 'text.secondary' }}>
                  <Cancel />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inativos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('clients.inactive')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.light', color: 'info.main' }}>
                  <PersonAdd />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.tipos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('clients.clientTypes')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label={t('common.search')}
              placeholder={t('clients.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="A">{t('clients.active')}</MenuItem>
              <MenuItem value="I">{t('clients.inactive')}</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.type')}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {clientTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={clients}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(0);
        }}
        onRefresh={fetchClients}
        onView={handleView}
        rowKey="id"
        emptyMessage={t('clients.noClients')}
      />

      {/* View Client Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              {selectedClient?.nome?.charAt(0)?.toUpperCase() || 'C'}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedClient?.nome}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('common.code')}: {selectedClient?.codigo}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseView}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)}>
            <Tab label={t('clients.generalData')} />
            <Tab label={t('clients.contacts')} />
            <Tab label={t('clients.documents')} />
            <Tab label={t('sidebar.payments')} />
          </Tabs>

          {loadingDetails ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('common.loading')}</Typography>
            </Box>
          ) : (
            <>
              <TabPanel value={viewTab} index={0}>
                {editMode ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('common.name')} value={editForm.nome}
                        onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('common.email')} value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('common.phone')} value={editForm.telefone}
                        onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('common.address')} value={editForm.morada}
                        onChange={(e) => setEditForm({ ...editForm, morada: e.target.value })} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.name')}</Typography>
                      <Typography variant="body1">{selectedClient?.nome}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.type')}</Typography>
                      <Typography variant="body1">{selectedClient?.tipo || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.username')}</Typography>
                      <Typography variant="body1">{selectedClient?.username || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.email')}</Typography>
                      <Typography variant="body1">{selectedClient?.email || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.country')}</Typography>
                      <Typography variant="body1">{selectedClient?.pais || 'Cabo Verde'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.address')}</Typography>
                      <Typography variant="body1">{selectedClient?.morada || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.dateRegister')}</Typography>
                      <Typography variant="body1">
                        {selectedClient?.dataRegisto ? new Date(selectedClient.dataRegisto).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedClient?.estado === 'A' ? t('common.active') : t('common.inactive')}
                          color={selectedClient?.estado === 'A' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </TabPanel>

              <TabPanel value={viewTab} index={1}>
                <List>
                  {selectedClient?.contactos?.length > 0 ? (
                    selectedClient.contactos.map((contacto: any) => (
                      <ListItem key={contacto.id}>
                        <ListItemIcon>
                          {contacto.tipo === 'Email' || contacto.tipoCodigo === 'Email' ? (
                            <Email color="primary" />
                          ) : (
                            <Phone color="primary" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={contacto.contacto}
                          secondary={contacto.tipo}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {t('clients.noContacts')}
                    </Typography>
                  )}
                </List>
              </TabPanel>

              <TabPanel value={viewTab} index={2}>
                <List>
                  {selectedClient?.documentos?.length > 0 ? (
                    selectedClient.documentos.map((doc: any) => (
                      <ListItem key={doc.id}>
                        <ListItemIcon>
                          <Badge color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.numero}
                          secondary={`${doc.tipo}${doc.tag ? ` - ${doc.tag}` : ''}`}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {t('clients.noDocuments')}
                    </Typography>
                  )}
                </List>
              </TabPanel>

              <TabPanel value={viewTab} index={3}>
                <List>
                  {selectedClient?.pagamentosRecentes?.length > 0 ? (
                    selectedClient.pagamentosRecentes.map((pag: any) => (
                      <ListItem key={pag.id}>
                        <ListItemIcon>
                          <Payment color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${pag.entidade} - ${pag.referencia}`}
                          secondary={`${new Date(pag.data).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })} - ${pag.estado}`}
                        />
                        <Typography variant="body2" fontWeight={600}>
                          {new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE' }).format(pag.valor)}
                        </Typography>
                      </ListItem>
                    ))
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {t('clients.noPayments')}
                    </Typography>
                  )}
                </List>
              </TabPanel>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {editMode ? (
            <>
              <Button onClick={() => setEditMode(false)}>{t('common.cancel')}</Button>
              <Button variant="contained" startIcon={<Save />} onClick={handleSaveEdit} disabled={savingEdit}>
                {savingEdit ? t('common.saving') : t('common.save')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseView}>{t('common.close')}</Button>
              <Button variant="outlined" startIcon={<LockReset />}
                color="warning"
                onClick={() => setResetConfirmOpen(true)}
                disabled={selectedClient?.estado !== 'A' || !selectedClient?.email}>
                {t('clients.resetPassword', 'Redefinir Senha')}
              </Button>
              <Button variant="outlined" startIcon={<PowerSettingsNew />}
                color={selectedClient?.estado === 'A' ? 'error' : 'success'}
                onClick={handleToggleStatus}>
                {selectedClient?.estado === 'A' ? t('clients.deactivate') : t('clients.activate')}
              </Button>
              <Button variant="contained" startIcon={<Edit />} onClick={handleStartEdit}>
                {t('common.edit')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('clients.resetPassword', 'Redefinir Senha')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('clients.resetPasswordConfirm', 'Tem a certeza que deseja enviar um email de redefinição de senha para')}
            {' '}<strong>{selectedClient?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('clients.resetPasswordNote', 'O link será válido durante 60 minutos.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} disabled={resetLoading}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="warning" onClick={handleResetPassword} disabled={resetLoading}
            startIcon={<LockReset />}>
            {resetLoading ? t('common.sending', 'A enviar...') : t('clients.sendResetEmail', 'Enviar Email')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Alerts */}
      {successMsg && (
        <Alert severity="success" sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setSuccessMsg('')}>{successMsg}</Alert>
      )}
      {errorMsg && (
        <Alert severity="error" sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setErrorMsg('')}>{errorMsg}</Alert>
      )}
    </Box>
  );
};

export default Clients;
